import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useNavigate } from 'react-router-dom'
import UserManagement from '../components/UserManagement'
import PersonnelManagement from '../components/PersonnelManagement'

// Matches PROGRAM_COLOR in Programs.js (.solid value) — used for the 8px stripe in the Programs tab list.
const PROGRAM_COLOR_HEX = {
  slate:   '#5F5E5A',
  blue:    '#378ADD',
  violet:  '#7C3AED',
  emerald: '#65A30D',
  amber:   '#D97706',
}

export default function Admin() {
  const { profile } = useAuth()
  const navigate = useNavigate()
  const [tab, setTab] = useState('categories')
  const [categories, setCategories] = useState([])
  const [questions, setQuestions] = useState([])
  const [resources, setResources] = useState({}) // { question_id: [resources] }
  const [sites, setSites] = useState([])
  const [programs, setPrograms] = useState([])
  const [programSiteCounts, setProgramSiteCounts] = useState({}) // { program_id: count }
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null)

  useEffect(() => {
    if (profile?.role !== 'admin') { navigate('/'); return }
    fetchAll()
  }, [profile])

  async function fetchAll() {
    const [catsRes, qsRes, sitesRes, resourcesRes, programsRes, psRes] = await Promise.all([
      supabase.from('categories').select('*').order('sort_order'),
      supabase.from('questions').select('*').order('sort_order'),
      supabase.from('sites').select('*').order('name'),
      supabase.from('question_resources').select('*'),
      supabase.from('programs').select('*').order('sort_order').order('created_at'),
      supabase.from('program_sites').select('program_id'),
    ])
    setCategories(catsRes.data || [])
    setQuestions(qsRes.data || [])
    setSites(sitesRes.data || [])
    setPrograms(programsRes.data || [])
    // Count sites per program
    const counts = {}
    for (const ps of (psRes.data || [])) {
      counts[ps.program_id] = (counts[ps.program_id] || 0) + 1
    }
    setProgramSiteCounts(counts)
    // Group resources by question_id
    const rMap = {}
    for (const r of (resourcesRes.data || [])) {
      if (!rMap[r.question_id]) rMap[r.question_id] = []
      rMap[r.question_id].push(r)
    }
    setResources(rMap)
    setLoading(false)
  }

  async function saveCategory(data) {
    if (data.id) await supabase.from('categories').update(data).eq('id', data.id)
    else await supabase.from('categories').insert({ ...data, sort_order: categories.length + 1 })
    fetchAll(); setModal(null)
  }

  async function deleteCategory(id) {
    if (!window.confirm('Delete this category and all its questions?')) return
    await supabase.from('categories').delete().eq('id', id)
    fetchAll()
  }

  async function saveQuestion(data) {
    if (data.id) await supabase.from('questions').update(data).eq('id', data.id)
    else {
      const count = questions.filter(q => q.category_id === data.category_id).length
      await supabase.from('questions').insert({ ...data, sort_order: count + 1 })
    }
    fetchAll(); setModal(null)
  }

  async function deleteQuestion(id) {
    if (!window.confirm('Delete this question?')) return
    await supabase.from('questions').delete().eq('id', id)
    fetchAll()
  }

  async function saveSite(data) {
    if (data.id) await supabase.from('sites').update(data).eq('id', data.id)
    else await supabase.from('sites').insert(data)
    fetchAll(); setModal(null)
  }

  async function saveProgram(data) {
    const payload = {
      slug: data.slug,
      name: data.name,
      description: data.description || null,
      color: data.color || 'slate',
      start_date: data.start_date || null,
      end_date: data.end_date || null,
      status: data.status || 'active',
      sort_order: Number.isFinite(data.sort_order) ? data.sort_order : 0,
    }
    let err
    if (data.id) {
      ({ error: err } = await supabase.from('programs').update(payload).eq('id', data.id))
    } else {
      ({ error: err } = await supabase.from('programs').insert(payload))
    }
    if (err) { alert('Save failed: ' + err.message); return }
    fetchAll(); setModal(null)
  }

  async function deleteProgram(program) {
    const siteCount = programSiteCounts[program.id] || 0
    const msg = siteCount > 0
      ? `Delete "${program.name}"?\n\nThis will also unlink ${siteCount} site${siteCount === 1 ? '' : 's'} and delete all tickets/dispatches under this program. This cannot be undone.`
      : `Delete "${program.name}"?\n\nThis cannot be undone.`
    if (!window.confirm(msg)) return
    const { error } = await supabase.from('programs').delete().eq('id', program.id)
    if (error) { alert('Delete failed: ' + error.message); return }
    fetchAll()
  }

  async function uploadResource(questionId, file) {
    const isImage = file.type.startsWith('image/')
    const isPdf = file.type === 'application/pdf'
    if (!isImage && !isPdf) { alert('Only images and PDF files are supported.'); return }

    const ext = file.name.split('.').pop()
    const path = `${questionId}/${Date.now()}-${file.name.replace(/[^a-zA-Z0-9._-]/g, '_')}`
    const { error: upErr } = await supabase.storage.from('question-resources').upload(path, file)
    if (upErr) { alert('Upload failed: ' + upErr.message); return }

    await supabase.from('question_resources').insert({
      question_id: questionId,
      file_name: file.name,
      file_type: isImage ? 'image' : 'document',
      storage_path: path,
    })
    fetchAll()
  }

  async function deleteResource(resource) {
    if (!window.confirm(`Delete "${resource.file_name}"?`)) return
    await supabase.storage.from('question-resources').remove([resource.storage_path])
    await supabase.from('question_resources').delete().eq('id', resource.id)
    fetchAll()
  }

  if (loading) return <div className="spinner" />

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Admin Panel</div>
          <div className="page-subtitle">Manage users, categories, questions, and sites</div>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab-btn ${tab === 'categories' ? 'active' : ''}`} onClick={() => setTab('categories')}>
          Categories ({categories.length})
        </button>
        <button className={`tab-btn ${tab === 'questions' ? 'active' : ''}`} onClick={() => setTab('questions')}>
          Questions ({questions.length})
        </button>
        <button className={`tab-btn ${tab === 'sites' ? 'active' : ''}`} onClick={() => setTab('sites')}>
          Sites ({sites.length})
        </button>
        <button className={`tab-btn ${tab === 'programs' ? 'active' : ''}`} onClick={() => setTab('programs')}>
          Programs ({programs.length})
        </button>
        <button className={`tab-btn ${tab === 'users' ? 'active' : ''}`} onClick={() => setTab('users')}>
          Users
        </button>
        <button className={`tab-btn ${tab === 'personnel' ? 'active' : ''}`} onClick={() => setTab('personnel')}>
          Personnel
        </button>
      </div>

      {/* PERSONNEL */}
      {tab === 'personnel' && <PersonnelManagement />}

      {/* USERS */}
      {tab === 'users' && (
        <UserManagement currentUserId={profile?.id} />
      )}

      {/* CATEGORIES */}
      {tab === 'categories' && (
        <div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
            onClick={() => setModal({ type: 'category', data: { name: '', icon: '', sort_order: categories.length + 1 } })}>
            + Add category
          </button>
          {categories.map(cat => (
            <div key={cat.id} className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 22 }}>{cat.icon}</span>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{cat.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {questions.filter(q => q.category_id === cat.id).length} questions · order {cat.sort_order}
                </div>
              </div>
              <span className={`badge ${cat.active ? 'badge-pass' : 'badge-na'}`}>{cat.active ? 'Active' : 'Hidden'}</span>
              <button className="btn btn-sm" onClick={() => setModal({ type: 'category', data: { ...cat } })}>Edit</button>
              <button className="btn btn-sm btn-danger" onClick={() => deleteCategory(cat.id)}>Delete</button>
            </div>
          ))}
        </div>
      )}

      {/* QUESTIONS */}
      {tab === 'questions' && (
        <div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
            onClick={() => setModal({ type: 'question', data: { label: '', hint: '', category_id: categories[0]?.id || '', sort_order: 0 } })}>
            + Add question
          </button>
          {categories.map(cat => {
            const qs = questions.filter(q => q.category_id === cat.id)
            return (
              <div key={cat.id} style={{ marginBottom: 24 }}>
                <h3 style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-muted)', marginBottom: 8 }}>
                  {cat.icon} {cat.name}
                </h3>
                {qs.map(q => {
                  const qResources = resources[q.id] || []
                  return (
                    <div key={q.id} className="card" style={{ marginBottom: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: 14 }}>{q.label}</div>
                          {q.hint && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{q.hint}</div>}

                          {/* Resource list */}
                          {qResources.length > 0 && (
                            <div style={{ marginTop: 8, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                              {qResources.map(r => (
                                <div key={r.id} style={{
                                  display: 'flex', alignItems: 'center', gap: 6,
                                  background: 'var(--bg-secondary)', borderRadius: 6,
                                  padding: '4px 8px', fontSize: 12,
                                }}>
                                  <span>{r.file_type === 'image' ? '🖼️' : '📄'}</span>
                                  <span style={{ color: 'var(--text-muted)', maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {r.file_name}
                                  </span>
                                  <button
                                    onClick={() => deleteResource(r)}
                                    style={{ background: 'none', border: 'none', color: 'var(--red)', cursor: 'pointer', fontSize: 14, lineHeight: 1, padding: 0 }}
                                  >×</button>
                                </div>
                              ))}
                            </div>
                          )}

                          {/* Upload resource */}
                          <label style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            marginTop: 8, fontSize: 12, color: 'var(--blue)',
                            cursor: 'pointer', padding: '4px 8px',
                            border: '1px dashed var(--blue-mid)', borderRadius: 6,
                          }}>
                            + Attach reference image or PDF
                            <input type="file" accept="image/*,.pdf" style={{ display: 'none' }}
                              onChange={e => e.target.files[0] && uploadResource(q.id, e.target.files[0])} />
                          </label>
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button className="btn btn-sm" onClick={() => setModal({ type: 'question', data: { ...q } })}>Edit</button>
                          <button className="btn btn-sm btn-danger" onClick={() => deleteQuestion(q.id)}>Del</button>
                        </div>
                      </div>
                    </div>
                  )
                })}
                {qs.length === 0 && (
                  <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '8px 0' }}>No questions yet.</div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* SITES */}
      {tab === 'sites' && (
        <div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
            onClick={() => setModal({ type: 'site', data: { name: '', address: '', city: '', state: 'MI', active: true } })}>
            + Add site
          </button>
          {sites.map(site => (
            <div key={site.id} className="card" style={{ marginBottom: 6, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 500 }}>{site.name}</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                  {[site.city, site.state].filter(Boolean).join(', ')}
                </div>
              </div>
              <span className={`badge ${site.active ? 'badge-pass' : 'badge-na'}`}>{site.active ? 'Active' : 'Inactive'}</span>
              <button className="btn btn-sm" onClick={() => setModal({ type: 'site', data: { ...site } })}>Edit</button>
            </div>
          ))}
        </div>
      )}

      {/* PROGRAMS */}
      {tab === 'programs' && (
        <div>
          <button className="btn btn-primary btn-sm" style={{ marginBottom: 12 }}
            onClick={() => setModal({
              type: 'program',
              data: { name: '', slug: '', description: '', color: 'slate', status: 'active',
                      start_date: '', end_date: '', sort_order: programs.length },
            })}>
            + Add program
          </button>
          {programs.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              No programs yet. Programs group sites together for dispatch, tickets, and reporting.
            </div>
          )}
          {programs.map(p => {
            const siteCount = programSiteCounts[p.id] || 0
            const statusClass =
              p.status === 'active'    ? 'badge-pass' :
              p.status === 'completed' ? 'badge-pass' :
              p.status === 'paused'    ? 'badge-warn' :
                                         'badge-na'
            return (
              <div key={p.id} className="card" style={{ marginBottom: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 8, height: 40, borderRadius: 2,
                  background: PROGRAM_COLOR_HEX[p.color] || PROGRAM_COLOR_HEX.slate,
                  flexShrink: 0,
                }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500 }}>{p.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                    {siteCount} site{siteCount === 1 ? '' : 's'} · {p.slug}
                    {p.start_date && ` · ${p.start_date}${p.end_date ? ` → ${p.end_date}` : ''}`}
                  </div>
                </div>
                <span className={`badge ${statusClass}`}>{p.status}</span>
                <button className="btn btn-sm" onClick={() => setModal({ type: 'program', data: { ...p } })}>Edit</button>
                <button className="btn btn-sm btn-danger" onClick={() => deleteProgram(p)}>Delete</button>
              </div>
            )
          })}
        </div>
      )}

      {/* MODALS */}
      {modal?.type === 'category' && <CategoryModal data={modal.data} onSave={saveCategory} onClose={() => setModal(null)} />}
      {modal?.type === 'question' && <QuestionModal data={modal.data} categories={categories} onSave={saveQuestion} onClose={() => setModal(null)} />}
      {modal?.type === 'site' && <SiteModal data={modal.data} onSave={saveSite} onClose={() => setModal(null)} />}
      {modal?.type === 'program' && <ProgramModal data={modal.data} existing={programs} onSave={saveProgram} onClose={() => setModal(null)} />}
    </div>
  )
}

function CategoryModal({ data, onSave, onClose }) {
  const [form, setForm] = useState(data)
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{form.id ? 'Edit category' : 'Add category'}</div>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Network / WiFi" />
        </div>
        <div className="form-group">
          <label className="form-label">Icon (emoji)</label>
          <input className="form-input" value={form.icon} onChange={e => setForm(p => ({ ...p, icon: e.target.value }))} placeholder="e.g. 📡" />
        </div>
        <div className="form-group">
          <label className="form-label">Sort order</label>
          <input className="form-input" type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) }))} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  )
}

function QuestionModal({ data, categories, onSave, onClose }) {
  const [form, setForm] = useState(data)
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{form.id ? 'Edit question' : 'Add question'}</div>
        <div className="form-group">
          <label className="form-label">Category</label>
          <select className="form-input" value={form.category_id} onChange={e => setForm(p => ({ ...p, category_id: e.target.value }))}>
            {categories.map(c => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Question label</label>
          <input className="form-input" value={form.label} onChange={e => setForm(p => ({ ...p, label: e.target.value }))} placeholder="e.g. Router online and accessible" />
        </div>
        <div className="form-group">
          <label className="form-label">Hint / helper text (optional)</label>
          <input className="form-input" value={form.hint || ''} onChange={e => setForm(p => ({ ...p, hint: e.target.value }))} placeholder="e.g. Check admin panel reachability" />
        </div>
        <div className="form-group">
          <label className="form-label">Sort order</label>
          <input className="form-input" type="number" value={form.sort_order} onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) }))} />
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  )
}

function SiteModal({ data, onSave, onClose }) {
  const [form, setForm] = useState(data)
  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-title">{form.id ? 'Edit site' : 'Add site'}</div>
        <div className="form-group">
          <label className="form-label">Site name</label>
          <input className="form-input" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Ann Arbor - Plymouth Rd" />
        </div>
        <div className="form-group">
          <label className="form-label">Address</label>
          <input className="form-input" value={form.address || ''} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} placeholder="123 Main St" />
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 80px', gap: 8 }}>
          <div className="form-group">
            <label className="form-label">City</label>
            <input className="form-input" value={form.city || ''} onChange={e => setForm(p => ({ ...p, city: e.target.value }))} placeholder="Ann Arbor" />
          </div>
          <div className="form-group">
            <label className="form-label">State</label>
            <input className="form-input" value={form.state || 'MI'} onChange={e => setForm(p => ({ ...p, state: e.target.value }))} />
          </div>
        </div>
        <div className="form-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.active} onChange={e => setForm(p => ({ ...p, active: e.target.checked }))} />
            <span className="form-label" style={{ margin: 0 }}>Active (visible to auditors)</span>
          </label>
        </div>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSave(form)}>Save</button>
        </div>
      </div>
    </div>
  )
}

function slugify(s) {
  return (s || '')
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

function ProgramModal({ data, existing, onSave, onClose }) {
  const [form, setForm] = useState(data)
  const [slugTouched, setSlugTouched] = useState(!!data.id) // don't auto-derive slug when editing
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Auto-derive slug from name on create (until user edits slug manually)
  function updateName(name) {
    setForm(p => ({
      ...p,
      name,
      slug: slugTouched ? p.slug : slugify(name),
    }))
  }

  // Client-side validation
  const trimmedName = (form.name || '').trim()
  const trimmedSlug = (form.slug || '').trim()
  const slugValid = /^[a-z0-9][a-z0-9-]*$/.test(trimmedSlug)
  const slugTaken = existing.some(p => p.slug === trimmedSlug && p.id !== form.id)
  const datesValid = !form.start_date || !form.end_date || form.start_date <= form.end_date

  const canSave = trimmedName && trimmedSlug && slugValid && !slugTaken && datesValid

  function handleSave() {
    if (!canSave) return
    onSave({ ...form, name: trimmedName, slug: trimmedSlug })
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 520 }}>
        <div className="modal-title">{form.id ? 'Edit program' : 'Add program'}</div>

        <div className="form-group">
          <label className="form-label">Name</label>
          <input className="form-input" value={form.name || ''}
            onChange={e => updateName(e.target.value)}
            placeholder="e.g. Q3 Hardware Refresh" />
        </div>

        <div className="form-group">
          <label className="form-label">Slug</label>
          <input className="form-input" value={form.slug || ''}
            onChange={e => { setSlugTouched(true); setForm(p => ({ ...p, slug: e.target.value })) }}
            placeholder="e.g. q3-hardware-refresh" />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Used in the program URL. Lowercase letters, numbers, and dashes only.
          </div>
          {trimmedSlug && !slugValid && (
            <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
              Slug must start with a letter or number and contain only lowercase letters, numbers, and dashes.
            </div>
          )}
          {slugValid && slugTaken && (
            <div style={{ fontSize: 12, color: 'var(--red)', marginTop: 4 }}>
              Another program already uses this slug.
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Description</label>
          <textarea className="form-input" rows={2} value={form.description || ''}
            onChange={e => setForm(p => ({ ...p, description: e.target.value }))}
            placeholder="Short description of what this program covers" />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group">
            <label className="form-label">Color</label>
            <select className="form-input" value={form.color || 'slate'}
              onChange={e => setForm(p => ({ ...p, color: e.target.value }))}>
              <option value="slate">Slate (gray)</option>
              <option value="blue">Blue</option>
              <option value="violet">Violet</option>
              <option value="emerald">Emerald</option>
              <option value="amber">Amber</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="form-input" value={form.status || 'active'}
              onChange={e => setForm(p => ({ ...p, status: e.target.value }))}>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <div className="form-group">
            <label className="form-label">Start date</label>
            <input className="form-input" type="date" value={form.start_date || ''}
              onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">End date</label>
            <input className="form-input" type="date" value={form.end_date || ''}
              onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} />
          </div>
        </div>
        {!datesValid && (
          <div style={{ fontSize: 12, color: 'var(--red)', marginTop: -8, marginBottom: 8 }}>
            Start date must be on or before end date.
          </div>
        )}

        {/* Advanced */}
        <button
          type="button"
          onClick={() => setShowAdvanced(v => !v)}
          style={{
            background: 'none', border: 'none', padding: 0,
            color: 'var(--text-muted)', fontSize: 12, cursor: 'pointer',
            marginBottom: showAdvanced ? 8 : 16,
          }}>
          {showAdvanced ? '▾' : '▸'} Advanced
        </button>
        {showAdvanced && (
          <div style={{
            borderLeft: '2px solid var(--border)', paddingLeft: 12, marginBottom: 12,
            fontSize: 13, color: 'var(--text-muted)',
          }}>
            <div className="form-group">
              <label className="form-label">Sort order</label>
              <input className="form-input" type="number" value={form.sort_order ?? 0}
                onChange={e => setForm(p => ({ ...p, sort_order: parseInt(e.target.value) || 0 }))} />
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                Lower numbers appear first in the Programs list.
              </div>
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, padding: '8px 0' }}>
              <strong>Hardware cost</strong> and <strong>cutover wave</strong> are set per-site when you link sites
              to this program (from the program's detail page), not on the program itself.
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={!canSave} onClick={handleSave}>Save</button>
        </div>
      </div>
    </div>
  )
}
